// lib/workspace-layer-sync.ts
import { getLayerWorkspaceId, normalizeLayerWorkspaceId } from '#types/types';
import type { Layer, Workspace } from '#types/types';

type WorkspaceLayerSyncInput = {
  layers: Layer[];
  workspaces: Workspace[];
};

type WorkspaceLayerSyncResult = {
  layers: Layer[];
  workspaces: Workspace[];
  layersChanged: boolean;
  workspacesChanged: boolean;
  workspacesNeedingEnsure: string[];
  issues: string[];
};

const areStringArraysEqual = (left: string[], right: string[]): boolean => {
  if (left === right) return true;
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return false;
  }
  return true;
};

const normalizeWorkspaceLayerIds = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  const deduped = new Set<string>();
  for (const entry of value) {
    if (typeof entry === 'string' && entry.trim().length > 0) {
      deduped.add(entry.trim());
    } else if (typeof entry === 'number' && Number.isFinite(entry)) {
      deduped.add(String(Math.trunc(entry)));
    }
  }
  return Array.from(deduped);
};

const normalizeVisibleLayerIds = (layerIds: string[]): string =>
  JSON.stringify([...layerIds].sort((a, b) => a.localeCompare(b)));

/**
 * Canonical matcher: which version of this workspace shows exactly this set of
 * visible layers? Several versions can share the same signature, so a
 * `preferredVersionId` that still matches wins over array order — keeping an
 * already-valid pointer in place instead of rewinding it.
 */
export const findMatchingVersionId = (
  workspace: Workspace | undefined,
  visibleLayerIds: string[],
  options?: { preferredVersionId?: string | null }
): string | null => {
  if (!workspace) return null;

  const versions = workspace.versions || [];
  const targetSignature = normalizeVisibleLayerIds(visibleLayerIds);
  const getVersionSignature = (version: (typeof versions)[number]): string =>
    normalizeVisibleLayerIds(version.layerVisibility.filter((item) => item.visible).map((item) => item.id));

  const preferredVersionId = typeof options?.preferredVersionId === 'string' && options.preferredVersionId.trim().length > 0
    ? options.preferredVersionId
    : null;

  if (preferredVersionId) {
    const preferredVersion = versions.find((version) => version.id === preferredVersionId) ?? null;
    if (preferredVersion && getVersionSignature(preferredVersion) === targetSignature) {
      return preferredVersion.id;
    }
  }

  const matchingVersion = versions.find((version) => getVersionSignature(version) === targetSignature);

  return matchingVersion ? matchingVersion.id : null;
};

export const syncWorkspaceLayerLinks = ({
  layers,
  workspaces,
}: WorkspaceLayerSyncInput): WorkspaceLayerSyncResult => {
  const issues: string[] = [];

  if (!Array.isArray(workspaces) || workspaces.length === 0) {
    if (layers.length > 0) {
      issues.push(`[WorkspaceLayerSync] No workspaces available for ${layers.length} layer(s).`);
    }
    return {
      layers,
      workspaces,
      layersChanged: false,
      workspacesChanged: false,
      workspacesNeedingEnsure: [],
      issues,
    };
  }

  const workspaceById = new Map(workspaces.map((workspace) => [workspace.id, workspace]));
  const workspacesByProjectId = new Map<string, Workspace[]>();
  workspaces.forEach((workspace) => {
    const projectWorkspaces = workspacesByProjectId.get(workspace.projectId) ?? [];
    projectWorkspaces.push(workspace);
    workspacesByProjectId.set(workspace.projectId, projectWorkspaces);
  });

  let layersChanged = false;
  const normalizedLayers = layers.map((layer) => {
    const normalizedLayerWorkspaceId = normalizeLayerWorkspaceId(getLayerWorkspaceId(layer));
    if (normalizedLayerWorkspaceId === null) {
      if (layer.layerWorkspaceId !== null) {
        layersChanged = true;
        return { ...layer, layerWorkspaceId: null };
      }
      return layer;
    }

    const layerProjectId = typeof layer.projectId === 'string' ? layer.projectId.trim() : '';
    const referencedWorkspace = workspaceById.get(normalizedLayerWorkspaceId);
    if (layerProjectId && referencedWorkspace?.projectId === layerProjectId) {
      if (normalizedLayerWorkspaceId !== layer.layerWorkspaceId) {
        layersChanged = true;
        return {
          ...layer,
          layerWorkspaceId: normalizedLayerWorkspaceId,
        };
      }
      return layer;
    }

    const sameProjectWorkspaces = workspacesByProjectId.get(layerProjectId) ?? [];
    const fallbackWorkspace = sameProjectWorkspaces.find((workspace) => workspace.id === '1')
      ?? [...sameProjectWorkspaces].sort((left, right) => left.id.localeCompare(right.id))[0];
    if (!fallbackWorkspace) {
      issues.push(
        `[WorkspaceLayerSync] Layer ${layer.id} has no workspace in project "${layerProjectId}"; it remains detached from workspace membership.`
      );
      return layer;
    }

    layersChanged = true;
    issues.push(
      `[WorkspaceLayerSync] Layer ${layer.id} had workspace "${normalizedLayerWorkspaceId}" outside project "${layerProjectId}". Reassigned to "${fallbackWorkspace.id}".`
    );
    return {
      ...layer,
      layerWorkspaceId: fallbackWorkspace.id,
    };
  });

  const layerIdsByWorkspace = new Map<string, string[]>();
  workspaces.forEach((workspace) => {
    layerIdsByWorkspace.set(workspace.id, []);
  });

  const sortedLayers = [...normalizedLayers].sort((left, right) => {
    const leftOrder = left.renderOrder ?? 0;
    const rightOrder = right.renderOrder ?? 0;
    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }
    return left.id.localeCompare(right.id);
  });

  sortedLayers.forEach((layer) => {
    const workspaceId = getLayerWorkspaceId(layer);
    if (!workspaceId) return;
    const workspace = workspaceById.get(workspaceId);
    if (!workspace || workspace.projectId !== (typeof layer.projectId === 'string' ? layer.projectId.trim() : '')) return;
    const list = layerIdsByWorkspace.get(workspaceId);
    if (!list) return;
    list.push(layer.id);
  });

  let workspacesChanged = false;
  const workspacesNeedingEnsure: string[] = [];
  const layersById = new Map(normalizedLayers.map((layer) => [layer.id, layer]));
  const normalizedWorkspaces = workspaces.map((workspace) => {
    const expectedLayerIds = layerIdsByWorkspace.get(workspace.id) ?? [];
    const currentLayerIds = normalizeWorkspaceLayerIds(workspace.layerIds);

    let workspaceDraft = workspace;

    if (!areStringArraysEqual(currentLayerIds, expectedLayerIds)) {
      workspacesChanged = true;
      issues.push(
        `[WorkspaceLayerSync] Workspace "${workspace.id}" layerIds healed (${currentLayerIds.length} -> ${expectedLayerIds.length}).`
      );
      workspaceDraft = {
        ...workspaceDraft,
        layerIds: expectedLayerIds,
      };
    } else if (workspace.layerIds !== currentLayerIds) {
      workspacesChanged = true;
      workspaceDraft = {
        ...workspaceDraft,
        layerIds: currentLayerIds,
      };
    }

    const visibleLayerIds = (workspaceDraft.layerIds || [])
      .filter((layerId) => layersById.get(layerId)?.visible)
      .sort((a, b) => a.localeCompare(b));
    const previousActiveVersionId = workspaceDraft.activeVersionId ?? null;
    const nextActiveVersionId = findMatchingVersionId(workspaceDraft, visibleLayerIds, {
      preferredVersionId: previousActiveVersionId,
    });

    if (previousActiveVersionId !== nextActiveVersionId) {
      workspacesChanged = true;
      workspaceDraft = {
        ...workspaceDraft,
        activeVersionId: nextActiveVersionId,
      };
      issues.push(
        `[WorkspaceLayerSync] Workspace "${workspace.id}" activeVersionId healed (${previousActiveVersionId ?? 'null'} -> ${nextActiveVersionId ?? 'null'}).`
      );
    } else if (workspaceDraft.activeVersionId === undefined) {
      workspacesChanged = true;
      workspaceDraft = {
        ...workspaceDraft,
        activeVersionId: nextActiveVersionId,
      };
    }

    if ((workspaceDraft.layerIds?.length ?? 0) > 0 && (workspaceDraft.activeVersionId ?? null) === null) {
      workspacesNeedingEnsure.push(workspaceDraft.id);
    }

    return workspaceDraft;
  });

  return {
    layers: layersChanged ? normalizedLayers : layers,
    workspaces: workspacesChanged ? normalizedWorkspaces : workspaces,
    layersChanged,
    workspacesChanged,
    workspacesNeedingEnsure,
    issues,
  };
};
